
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./auth-preview.css";

type AuthLayoutProps = {
    children: React.ReactNode;
    showWelcomePanel?: boolean;
};

const AuthWelcomePanel = () => {
    const { t } = useTranslation(["auth"]);
    const gridRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        const values = gridRef.current?.querySelectorAll<HTMLElement>("[data-countup]");
        if (!values?.length) return;

        const start = performance.now();
        let frame = 0;
        const tick = (time: number) => {
            let animating = false;
            values.forEach((element, index) => {
                const progress = Math.min(1, Math.max(0, (time - start - (300 + index * 200)) / 1400));
                const amount = Number(element.dataset.countup) * (1 - (1 - progress) ** 3);
                const format = element.dataset.countupFormat;
                element.textContent = format === "currency"
                    ? `$${Math.round(amount).toLocaleString("en-US")}`
                    : format === "compact-currency"
                        ? `$${amount.toFixed(1)}k`
                        : `${Math.round(amount)}%`;
                animating ||= progress < 1;
            });
            if (animating) frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);

        return () => window.cancelAnimationFrame(frame);
    }, []);

    return (
        <aside
            aria-label={t("auth.brand.previewAlt")}
            role="img"
            className="hidden min-h-[calc(100dvh-2rem)] flex-col justify-center overflow-hidden rounded-[var(--ds-radius-xl)] bg-[var(--ds-card-lilac)] p-6 text-[var(--ds-on-card)] xl:flex xl:p-14"
        >
            <div className="mx-auto w-full max-w-2xl">
                <p className="text-xs font-medium leading-4 uppercase tracking-[0.08em]">{t("auth.brand.previewKicker")}</p>
                <h2 className="mt-3 max-w-lg whitespace-pre-line font-[var(--ds-font-display)] text-[40px] font-normal leading-none tracking-[-0.04em] xl:text-[56px]">
                    {t("auth.brand.previewHeadline")}
                </h2>
                <p className="mt-4 max-w-lg text-[15px] leading-[22px] md:text-[18px] md:leading-7">
                    {t("auth.brand.previewSubheadline")}
                </p>

                <div ref={gridRef} className="auth-preview-grid mt-8 grid grid-cols-2 grid-rows-[1.1fr_1fr] gap-3 sm:mt-10 sm:gap-4" aria-hidden="true">
                    <section className="auth-preview-card auth-preview-card-one col-span-2 flex min-h-40 flex-col justify-between rounded-[var(--ds-radius-lg)] bg-[var(--ds-card-sun)] p-5 sm:min-h-52 sm:p-7">
                        <p className="text-xs font-medium leading-4 uppercase tracking-[0.08em]">{t("auth.brand.previewRemaining")}</p>
                        <div>
                            <p className="auth-preview-count font-[var(--ds-font-display)] text-[56px] font-normal leading-none tracking-[-0.04em] sm:text-[64px] xl:text-[88px]" data-countup="2400" data-countup-format="currency">$2,400</p>
                            <p className="mt-2 text-[15px] leading-[22px]">{t("auth.brand.previewCycle")}</p>
                        </div>
                    </section>
                    <section className="auth-preview-card auth-preview-card-two flex min-h-36 flex-col justify-between rounded-[var(--ds-radius-lg)] bg-[var(--ds-card-flame)] p-4 sm:min-h-44 sm:p-6">
                        <p className="text-xs font-medium leading-4 uppercase tracking-[0.08em]">{t("auth.brand.previewSpending")}</p>
                        <div>
                            <p className="auth-preview-count font-[var(--ds-font-display)] text-[36px] font-normal leading-none tracking-[-0.04em] sm:text-[44px] xl:text-[52px]" data-countup="9.7" data-countup-format="compact-currency">$9.7k</p>
                            <p className="mt-2 text-[15px] leading-[22px]">{t("auth.brand.previewSpendingChange")}</p>
                        </div>
                    </section>
                    <section className="auth-preview-card auth-preview-card-three flex min-h-36 flex-col justify-between rounded-[var(--ds-radius-lg)] bg-[var(--ds-ground)] p-4 sm:min-h-44 sm:p-6">
                        <p className="text-xs font-medium leading-4 uppercase tracking-[0.08em] text-[var(--ds-ink-muted)]">{t("auth.brand.previewGoal")}</p>
                        <div>
                            <p className="auth-preview-count font-[var(--ds-font-display)] text-[36px] font-normal leading-none tracking-[-0.04em] sm:text-[44px] xl:text-[52px]" data-countup="68" data-countup-format="percent">68%</p>
                            <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--ds-line)]">
                                <div className="auth-preview-progress h-full w-[68%] rounded-full bg-[var(--ds-card-sun)]" />
                            </div>
                            <p className="mt-2 text-[15px] leading-[22px] text-[var(--ds-ink-muted)]">{t("auth.brand.previewGoalAmount")}</p>
                        </div>
                    </section>
                </div>
            </div>
        </aside>
    );
};

export const AuthLayout = ({ children, showWelcomePanel = false }: AuthLayoutProps) => {
    const { t } = useTranslation(["auth"]);

    if (showWelcomePanel) {
        return (
            <main className="grid min-h-dvh grid-cols-1 gap-4 bg-[var(--app-page-bg)] text-[var(--app-text)] xl:grid-cols-[minmax(0,1fr)_minmax(0,680px)] xl:p-4">
                <section className="flex min-h-dvh flex-col px-4 pb-8 pt-4 sm:px-8 xl:min-h-[calc(100dvh-2rem)] xl:px-4 xl:py-3">
                    <a href="/" aria-label="HeyJerry!" className="hidden w-fit items-center gap-2 text-[var(--app-text)] xl:inline-flex">
                        <img src="/brand/jerry-j-mark-ink.svg" alt="" aria-hidden="true" className="app-nav-brand-mark app-nav-brand-mark-light h-9 w-10 object-contain" />
                        <img src="/brand/jerry-j-mark-crema.svg" alt="" aria-hidden="true" className="app-nav-brand-mark app-nav-brand-mark-dark h-9 w-10 object-contain" />
                        <span className="font-[var(--ds-font-display)] text-[26px] font-medium leading-none tracking-[-0.04em]">HeyJerry!</span>
                    </a>
                    <header className="-mx-4 -mt-4 mb-8 flex items-center gap-2 rounded-b-[var(--ds-radius-xl)] bg-[var(--ds-card-lilac)] px-6 py-7 text-[var(--ds-on-card)] sm:-mx-8 sm:px-8 xl:hidden">
                        <img src="/brand/jerry-face-mark-ink.svg" alt="" aria-hidden="true" className="h-7 w-10 object-contain" />
                        <span className="font-[var(--ds-font-display)] text-[22px] font-medium leading-none tracking-[-0.04em]">HeyJerry!</span>
                    </header>
                    <div className="flex flex-1 items-center justify-center xl:py-10">
                        {children}
                    </div>
                </section>
                <AuthWelcomePanel />
            </main>
        );
    }

    return (
        <main className="relative grid min-h-screen place-items-center overflow-hidden bg-[var(--app-page-bg)] p-4 text-[var(--app-text)] sm:p-8">
            <section className="relative grid w-full max-w-5xl gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                <aside className="hidden lg:block">
                    <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--app-muted-text)]">{t("auth.brand.kicker")}</p>
                    <h1 className="max-w-sm font-[var(--ds-font-display)] text-6xl font-semibold leading-[0.92] tracking-[-0.055em] text-[var(--app-text)]">
                        {t("auth.brand.headline")}
                    </h1>
                    <p className="mt-6 max-w-md text-base leading-7 text-[var(--app-text-secondary)]">
                        {t("auth.brand.subheadline")}
                    </p>
                </aside>
                <div className="w-full justify-self-center lg:justify-self-end">
                    {children}
                </div>
            </section>
        </main>
    )
}
